/**
 * useShellHouseRules - the table's rulings, resolved once per render.
 *
 * Every session-wide house rule the shell threads into its hooks and
 * panels, read through the resolvers (`engine/settings/resolve`), which stay
 * the authority. Resolve ONCE at the top and pass it down - never inject a
 * session value into `projectDefender` (see gotchas). The object-valued
 * answers are frozen singletons behind their resolvers, so they are safe in
 * dependency arrays.
 *
 * Also: the GM's inline writers for two rules (null for a player, so the
 * control is absent rather than dead), the log yielding its column while the
 * map builds or deploys, and the stamp a bug report carries. Extracted from
 * DeathWatchSimulator.
 */
import { useEffect, useCallback } from 'react';
import * as multiplayer from '../multiplayer';
import * as settings from '../engine/settings/resolve';
import { changedSessionRules } from '../engine/settings/registry';
import { diag } from '../diagnostics/log';
import { signatureReachOf } from '../engine/stealth/noise';
import { coverReachMetres } from '../engine/battlefield/coverNearTarget';

/**
 * The shell's house rules, as one record. Pure: the same permissions (and
 * the terrain's legacy flag) always give the same answers. Over the length
 * target on purpose: it is a declarative table, one rule per line.
 */
export function resolveTableRules(permissions, terrain) {
  return {
    // Both weapon-special pickers (Slot editor + the character's Weapons tab).
    allowCrossTypeSpecials: settings.resolveUnrestrictedWeaponSpecials(permissions),
    // How a cover pinned by hand and the map's own answer meet (engine/coverConflict).
    coverConflictMode: settings.resolveCoverConflictMode(permissions),
    // How near the target cover must stand to count, in metres; null = anywhere on the line (2026-10-25).
    coverReach: coverReachMetres(settings.resolveCoverNearTarget(permissions)),
    // DOES TERRAIN ADJUDICATE ANYTHING - a table ruling, not a view option
    // (v8.63.0). It lived on the terrain document, and the resolver still
    // honours that old home for a session that set it there.
    ignoreTerrain: settings.resolveIgnoreTerrain(permissions, terrain?.ignoreTerrain),
    logYieldsInBuild: settings.resolveLogYieldsInBuild(permissions),
    // Sheet tests, attack rolls, reactions and both probability engines.
    unnaturalDosMode: settings.resolveUnnaturalDosMode(permissions),
    // How loudly this client hears an owed reaction - not who sees one.
    reactionAlarmPolicy: settings.resolveReactionAlarm(permissions),
    // Does a missed blast go somewhere, and can it come back to the thrower?
    blastScatterOn: settings.resolveBlastScatter(permissions),
    blastSelfPolicy: settings.resolveBlastCatchesAttacker(permissions),
    // Reliable: official 00-then-confirm by default; this restores never-jams.
    reliableNeverJamsOn: settings.resolveReliableNeverJams(permissions),
    // Conditions modify tests and reactions (default on), and which of them.
    applyConditionModifiers: settings.resolveApplyConditionModifiers(permissions),
    condModOptions: settings.resolveConditionModOptions(permissions),
    showActionEconomy: settings.resolveShowActionEconomy(permissions),
    // What each helper adds to a called test and to a group approach, as a number.
    assistBonus: Number(settings.resolveAssistBonus(permissions)),
    // How far each Signature step carries, metres (plan armoury): who hears an attack in Stealth, the ring it rings.
    signatureReach: signatureReachOf(settings.resolveSignatureReach(permissions)),
    // Timed conditions lose a round per tracked turn (session-wide since v7.10.0).
    roundCountdown: settings.resolveRoundCountdown(permissions),
    stackPolicy: settings.resolveStackPolicy(permissions),
    // The table half of the Protagonist gate: off, every protagonist field is inert.
    protagonistRules: settings.resolveProtagonistRules(permissions),
    averagedDamage: settings.resolveAveragedDamage(permissions),
    // May a measurement write its cover and range band onto the target?
    battlefieldDerivedValues: settings.resolveBattlefieldDerivedValues(permissions),
    autoApplyMapModifiers: settings.resolveAutoApplyMapModifiers(permissions),
    avoidOnDeploy: settings.resolveDeployAvoidance(permissions),
    surfacesModifyCombatants: settings.resolveSurfacesModifyCombatants(permissions),
    redeployOnMapLoad: settings.resolveRedeployOnMapLoad(permissions),
    vehicleArcPreset: settings.resolveVehicleArcPreset(permissions),
  };
}

/** A GM's inline writer for one house rule: through the shared helper, since setPermissions is a full REPLACE. */
function useHouseRuleWriter(sessionId, permissions, key) {
  return useCallback(
    (value) => {
      if (!sessionId) return;
      multiplayer.writeHouseRule(sessionId, permissions, key, value).catch(console.error);
    },
    [sessionId, permissions, key],
  );
}

/**
 * @param {object} args - permissions, terrain, sessionId, playerId, isGm,
 *   mapWantsColumn (Build or Deploy wants the log's column), setLogYield.
 * @returns the resolved rules plus `onIgnoreTerrainChange` and
 *   `onToggleCrossTypeSpecials` (each null for a player).
 */
export default function useShellHouseRules({
  permissions,
  terrain,
  sessionId,
  playerId,
  isGm,
  mapWantsColumn,
  setLogYield,
}) {
  const rules = resolveTableRules(permissions, terrain);
  const writeIgnoreTerrain = useHouseRuleWriter(sessionId, permissions, 'ignoreTerrain');
  const writeCrossTypeSpecials = useHouseRuleWriter(sessionId, permissions, 'unrestrictedWeaponSpecials');
  // THE LOG YIELDS WHILE BUILDING - AND WHILE DEPLOYING (v8.0.1, v8.23.0).
  // Stated rather than switched, so turning the rule off mid-Build gives the
  // log back on the spot (v8.36.0); the hook's yield is idempotent.
  const { logYieldsInBuild } = rules;
  useEffect(() => {
    setLogYield(mapWantsColumn && logYieldsInBuild);
  }, [mapWantsColumn, logYieldsInBuild, setLogYield]);
  // The stamp a bug report carries: which session, which seat, which rules.
  useEffect(() => {
    diag.setContext({ sessionId, playerId, isGm, houseRules: changedSessionRules(permissions?.houseRules) });
  }, [sessionId, playerId, isGm, permissions]);
  return {
    ...rules,
    onIgnoreTerrainChange: isGm ? writeIgnoreTerrain : null,
    onToggleCrossTypeSpecials: isGm ? writeCrossTypeSpecials : null,
  };
}
